import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PopupNames } from 'core';
import {
  useAllFakturowniaInvoices,
  usePopupStateOperations,
  useStudentPermissions,
} from 'core/hooks';
import type { IStudentAlertTarget } from 'core/utils/studentAlerts';

import * as S from '../FakturowniaInvoices/FakturowniaInvoices.styled';
import * as L from './StudentDetail.styled';
import { IStudent } from './parts';
import { IdentityAnchor } from './parts/IdentityAnchor';
import { MoveToGroupInlinePopup } from './parts/MoveToGroupInlinePopup';

interface IHeaderBarProps {
  student: IStudent;
  /** Scroll to the orphan-invoices block (Link Invoice action). */
  onScrollToOrphans?: () => void;
  /** Atomic deep-link navigation, used by the data-driven Link Invoice action. */
  onNavigate?: (target: IStudentAlertTarget) => void;
}

/**
 * Student card header: identity anchor + name/email/phone, and a row of
 * role-and-data gated quick actions (Area 4, Req 9–13).
 *
 * - «Link Invoice» appears only when a FRESH orphan-invoice lookup finds ≥1
 *   orphan (no hardcoded flag, Req 9.1–9.3); selecting it deep-links to the
 *   orphan block, navigating even if none currently exist (Req 9.4).
 * - Quick actions (add agreement, record payment, issue invoice, change group,
 *   contact by email) open existing flows without leaving the card (Req 10.8);
 *   each is hidden when the operator's role lacks the capability (Req 10.7) and
 *   money actions are disabled without an active agreement (Req 11).
 */
export const HeaderBar = memo(
  ({ student, onScrollToOrphans, onNavigate }: IHeaderBarProps) => {
    const { t } = useTranslation();
    const {
      operations: { showPopup },
    } = usePopupStateOperations();
    const permissions = useStudentPermissions();
    const [moving, setMoving] = useState(false);

    const fullName = useMemo(
      () =>
        [
          student.user?.firstName,
          student.user?.middleName,
          student.user?.lastName,
        ]
          .filter(Boolean)
          .join(' ') || '—',
      [student.user],
    );

    const email = student.user?.email || '';

    // Fresh orphan-invoice presence on every render — no cached flag (Req 9.3).
    const { data: invoices } = useAllFakturowniaInvoices({
      studentId: student.id,
      direction: 'issued',
    });
    const hasOrphanInvoices = useMemo(() => invoices.some((r) => !r.payment), [
      invoices,
    ]);

    const activeAgreements = useMemo(
      () => (student.agreements || []).filter((a) => a && !a.terminated),
      [student.agreements],
    );
    const hasActiveAgreement = activeAgreements.length > 0;

    const onAddAgreement = useCallback(() => {
      showPopup(PopupNames.studentPayment, { studentId: student.id });
    }, [showPopup, student.id]);

    const onRecordPayment = useCallback(() => {
      // Record-payment shares the financial entry popup; the schedule-first
      // model resolves the contribution inside the flow.
      showPopup(PopupNames.studentPayment, { studentId: student.id });
    }, [showPopup, student.id]);

    const onIssueInvoice = useCallback(() => {
      showPopup(PopupNames.studentPayment, { studentId: student.id });
    }, [showPopup, student.id]);

    const onLinkInvoice = useCallback(() => {
      if (onNavigate) onNavigate({ tab: 'overview', block: 'orphanInvoices' });
      else if (onScrollToOrphans) onScrollToOrphans();
    }, [onNavigate, onScrollToOrphans]);

    // Change-group: the single active agreement is moved directly; with more
    // than one the operator picks which agreement via the inline popup (Req 11.3).
    const moveAgreement = activeAgreements[0] || null;

    return (
      <>
        <S.HeaderRow>
          <IdentityRow>
            <IdentityAnchor fullName={fullName} />
            <S.PageTitle>{fullName}</S.PageTitle>
          </IdentityRow>
          <L.HeaderActions>
            {permissions.canAddAgreement ? (
              <L.PrimaryActionButton
                type="button"
                tone="primary"
                onClick={onAddAgreement}
              >
                {t('pages.studentDetail.actions.addAgreement')}
              </L.PrimaryActionButton>
            ) : null}

            {permissions.canRecordPayment ? (
              <L.PrimaryActionButton
                type="button"
                onClick={onRecordPayment}
                disabled={!hasActiveAgreement}
                title={
                  hasActiveAgreement
                    ? undefined
                    : t('pages.studentDetail.actions.noActiveAgreement')
                }
              >
                {t('pages.studentDetail.actions.recordPayment')}
              </L.PrimaryActionButton>
            ) : null}

            {permissions.canIssueInvoice ? (
              <L.PrimaryActionButton
                type="button"
                onClick={onIssueInvoice}
                disabled={!hasActiveAgreement}
                title={
                  hasActiveAgreement
                    ? undefined
                    : t('pages.studentDetail.actions.noActiveAgreement')
                }
              >
                {t('pages.studentDetail.actions.issueInvoice')}
              </L.PrimaryActionButton>
            ) : null}

            {permissions.canChangeGroup && moveAgreement ? (
              <L.PrimaryActionButton
                type="button"
                onClick={() => setMoving(true)}
              >
                {t('pages.studentDetail.actions.changeGroup')}
              </L.PrimaryActionButton>
            ) : null}

            {hasOrphanInvoices ? (
              <L.PrimaryActionButton type="button" onClick={onLinkInvoice}>
                {t('pages.studentDetail.actions.linkInvoice')}
              </L.PrimaryActionButton>
            ) : null}

            {permissions.canContactEmail && email ? (
              <L.PrimaryActionButton
                as="a"
                href={`mailto:${email}`}
                tone={undefined}
              >
                {t('pages.studentDetail.actions.contactEmail')}
              </L.PrimaryActionButton>
            ) : null}
          </L.HeaderActions>
        </S.HeaderRow>
        <S.SubTitle>
          {email || '—'}
          {student.user?.phone ? ` · ${student.user.phone}` : ''}
        </S.SubTitle>

        {moving && moveAgreement ? (
          <MoveToGroupInlinePopup
            agreement={moveAgreement}
            studentId={student.id}
            onClose={() => setMoving(false)}
          />
        ) : null}
      </>
    );
  },
);

const IdentityRow = ({ children }: { children: React.ReactNode }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 12,
    }}
  >
    {children}
  </span>
);
